import { useState, useEffect } from 'react'
import { Link, Outlet, useLocation } from 'react-router-dom'
import { Menu, X } from 'lucide-react'
import scribenzaLogo from '@/assets/scribenza-logo.png'

export default function PublicLayout() {
  const [scrolled,    setScrolled]    = useState(false)
  const [mobileOpen,  setMobileOpen]  = useState(false)
  const location = useLocation()

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30)
    window.addEventListener('scroll', onScroll)
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  useEffect(() => { setMobileOpen(false) }, [location.pathname])

  const navLinks = [
    { to: '/features', label: 'Features' },
    { to: '/pricing', label: 'Pricing' },
    { to: '/about', label: 'About' },
    { to: '/contact', label: 'Contact' },
  ]

  return (
    <div className="pub-font-body" style={{ background: 'var(--pub-bg)', color: 'var(--pub-text)', minHeight: '100vh' }}>
      {/* ── Nav ─────────────────────────────────────────────────────── */}
      <nav style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100,
        transition: 'all 0.3s',
        background: scrolled ? 'rgba(6,8,14,0.92)' : 'transparent',
        backdropFilter: scrolled ? 'blur(20px)' : 'none',
        borderBottom: scrolled ? '1px solid #1E284530' : '1px solid transparent',
      }}>
        <div className="pub-nav-container" style={{ maxWidth: 1120, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          {/* Logo */}
          <a href="/" onClick={e => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); if (location.pathname !== '/') window.location.href = '/' }} style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
            <img src={scribenzaLogo} alt="Scribenza" style={{ width: 130, height: 42, objectFit: 'contain', display: 'block' }} />
          </a>

          {/* Desktop links */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 32 }} className="hidden-mobile">
            {navLinks.map(l => {
              const isActive = location.pathname === l.to
              return (
                <a key={l.to} href={l.to} style={{
                  textDecoration: 'none', color: isActive ? 'var(--pub-teal)' : 'var(--pub-muted)', fontSize: 14,
                  fontWeight: isActive ? 600 : 500, transition: 'color 0.15s',
                  borderBottom: isActive ? '2px solid var(--pub-teal)' : '2px solid transparent',
                  paddingBottom: 4,
                }}
                  onMouseEnter={e => { if (!isActive) e.target.style.color = 'var(--pub-text)' }}
                  onMouseLeave={e => { if (!isActive) e.target.style.color = 'var(--pub-muted)' }}>
                  {l.label}
                </a>
              )
            })}
          </div>

          {/* Desktop CTA */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }} className="hidden-mobile">
            <Link to="/login" style={{
              textDecoration: 'none', color: 'var(--pub-muted)', fontSize: 14,
              fontWeight: 500, padding: '8px 16px', transition: 'color 0.15s',
            }}>Sign in</Link>
            <Link to="/register" className="pub-btn-primary" style={{ padding: '9px 20px', fontSize: 14, textDecoration: 'none' }}>
              Get started free
            </Link>
          </div>

          {/* Mobile menu button */}
          <button onClick={() => setMobileOpen(!mobileOpen)}
            style={{ background: 'none', border: 'none', color: 'var(--pub-text)', cursor: 'pointer', padding: 4 }}
            className="show-mobile">
            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>

        {/* Mobile dropdown */}
        {mobileOpen && (
          <div style={{
            background: 'rgba(13,17,32,0.98)', borderTop: '1px solid var(--pub-border)',
            padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 4,
          }}>
            {navLinks.map(l => (
              <a key={l.to} href={l.to} style={{
                textDecoration: 'none', color: 'var(--pub-muted)',
                fontSize: 15, fontWeight: 500, padding: '10px 0',
                borderBottom: '1px solid var(--pub-border)',
              }}>
                {l.label}
              </a>
            ))}
            <div style={{ display: 'flex', gap: 12, marginTop: 12 }}>
              <Link to="/login" style={{
                flex: 1, textAlign: 'center', textDecoration: 'none',
                padding: '11px', border: '1px solid var(--pub-border)',
                borderRadius: 10, color: 'var(--pub-text)', fontSize: 14, fontWeight: 500,
              }}>Sign in</Link>
              <Link to="/register" style={{
                flex: 1, textAlign: 'center', textDecoration: 'none',
                padding: '11px', borderRadius: 10, fontSize: 14, fontWeight: 600,
                background: 'linear-gradient(135deg,#533AB7,#00D4B5)', color: '#fff',
              }}>Get started</Link>
            </div>
          </div>
        )}
      </nav>

      {/* Page content */}
      <Outlet />

      {/* ── Footer ──────────────────────────────────────────────────── */}
      <footer style={{ borderTop: '1px solid var(--pub-border)', padding: '60px 24px 40px', background: '#080A12' }}>
        <div style={{ maxWidth: 1120, margin: '0 auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))', gap: 48, marginBottom: 48 }}>
            {/* Brand */}
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                <img src={scribenzaLogo} alt="Scribenza" style={{ width: 110, height: 32, objectFit: 'contain' }} />
              </div>
              <p style={{ fontSize: 13, color: 'var(--pub-muted)', lineHeight: 1.7, maxWidth: 220 }}>
                Automating clinical documentation. AI clinical notes, instantly.
              </p>
            </div>

            {[
              { title: 'Product', links: [{ label: 'Features', href: '/features' }, { label: 'Pricing', href: '/pricing' }, { label: 'Book a demo', href: '/demo' }] },
              { title: 'Company', links: [{ label: 'About', href: '/about' }, { label: 'Contact', href: '/contact' }, { label: 'Security', href: '/security' }] },
              { title: 'Legal', links: [{ label: 'Terms of Service', href: '/terms' }, { label: 'Privacy Policy', href: '/privacy' }] },
              { title: 'Account', links: [{ label: 'Sign in', href: '/login' }, { label: 'Create account', href: '/register' }] },
            ].map(col => (
              <div key={col.title}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--pub-faint)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 14 }}>{col.title}</div>
                {col.links.map(l => (
                  <a key={l.label} href={l.href} style={{ display: 'block', textDecoration: 'none', color: 'var(--pub-muted)', fontSize: 13, marginBottom: 10, transition: 'color 0.15s' }}
                    onMouseEnter={e => e.target.style.color = 'var(--pub-teal)'}
                    onMouseLeave={e => e.target.style.color = 'var(--pub-muted)'}>
                    {l.label}
                  </a>
                ))}
              </div>
            ))}
          </div>

          <div style={{ borderTop: '1px solid var(--pub-border)', paddingTop: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
            <p style={{ fontSize: 12, color: 'var(--pub-faint)' }}>© 2026 Scribenza. All rights reserved.</p>
            <p style={{ fontSize: 12, color: 'var(--pub-faint)' }}>Built with Claude AI · OpenAI Whisper</p>
          </div>
        </div>
      </footer>

      <style>{`
        .pub-nav-container { padding: 24px; }
        @media (max-width: 768px) {
          .pub-nav-container { padding: 16px 20px; }
          .hidden-mobile { display: none !important; }
          .show-mobile   { display: block !important; }
        }
        @media (min-width: 769px) {
          .show-mobile { display: none !important; }
        }
      `}</style>
    </div>
  )
}
